import { captureException } from "@sentry/react";
import axios from "axios";
import useNotifications from "features/Notifications/hooks/useNotifications";
import useProject from "hooks/useProject";
import { useCallback, useState } from "react";
import { mutate } from "swr";
import { VidletApiError } from "types/Errors";

export interface ApiWriteResponse<R = unknown> {
  data: R;
  status: number;
}

interface Params {
  url: string;
  method: "delete" | "post" | "put" | "patch";
  mutateOnSuccess?: string[];
  data?: unknown;
  allowForImports?: boolean;
  suppressErrorNotification?: boolean;
  /** When true, the Axios request interceptor omits the Bearer token. */
  skipAuth?: boolean;
}

type WriteFn = <R = unknown>(params: Params) => Promise<ApiWriteResponse<R>>;

export default function useApiWrite() {
  const { imported } = useProject();
  const [isWriting, setIsWriting] = useState(false);
  const [error, setError] = useState<VidletApiError | undefined>();
  const { setNotification } = useNotifications();

  const write = useCallback(
    (async (params: Params) => {
      const {
        allowForImports,
        data,
        method,
        url,
        mutateOnSuccess,
        suppressErrorNotification,
        skipAuth,
      } = params;

      if (imported && !allowForImports) {
        const READ_ONLY_MESSAGE =
          "This function is not allowed for imported projects.";

        setNotification({
          message: READ_ONLY_MESSAGE,
          severity: "error",
        });

        throw new Error(READ_ONLY_MESSAGE);
      }

      try {
        setIsWriting(true);
        const requestConfig = skipAuth ? { skipAuth: true } : undefined;
        const result =
          method === "delete"
            ? await axios.delete(url, requestConfig)
            : await axios[method](url, data, requestConfig);

        mutateOnSuccess?.forEach((url) => {
          mutate(url);
        });
        return { data: result.data, status: result.status };
      } catch (caught) {
        const error = caught as {
          response?: {
            status?: number;
            data?: {
              message?: string;
              error?: { message: string; errors: string[] };
            };
          };
        };
        setError(error as VidletApiError);
        if (!suppressErrorNotification) {
          let message = "An error has occurred. Please try again later.";
          const errors = error.response?.data?.error?.errors;

          if (error.response?.data?.message) {
            message = error.response.data.message;
            captureException(
              new Error(`Unexpected error message path detected`),
              {
                extra: { error },
              },
            );
          } else if (error.response?.data?.error?.message)
            message = error.response.data.error.message;

          setNotification({ message, errors, severity: "error" });
        }
        throw caught;
      } finally {
        setIsWriting(false);
      }
    }) as WriteFn,
    [imported, setNotification],
  );

  return {
    createNotificationFromResponse: useCallback(
      (response: { message: string }): void => {
        setNotification({ message: response.message });
      },
      [setNotification],
    ),
    write,
    error,
    isWriting,
  };
}
